import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { errorMessage } from '../lib/api';

type Severity = 'success' | 'error' | 'info' | 'warning';
interface ToastApi {
  success: (msg: string) => void;
  error: (errOrMsg: unknown) => void;
  info: (msg: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<{ msg: string; severity: Severity; key: number } | null>(null);
  const show = useCallback((msg: string, severity: Severity) => setToast({ msg, severity, key: Date.now() }), []);
  const api: ToastApi = {
    success: useCallback((m: string) => show(m, 'success'), [show]),
    error: useCallback((e: unknown) => show(typeof e === 'string' ? e : errorMessage(e), 'error'), [show]),
    info: useCallback((m: string) => show(m, 'info'), [show]),
  };
  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        key={toast?.key}
        open={!!toast}
        autoHideDuration={toast?.severity === 'error' ? 7000 : 3500}
        onClose={(_e, reason) => reason !== 'clickaway' && setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity ?? 'info'} variant="filled" onClose={() => setToast(null)} sx={{ minWidth: 300 }}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return ctx;
}
